import { useState } from 'react';
import useTranslation from 'next-translate/useTranslation';
import Trans from 'next-translate/Trans';
import { useIsFetching, useQueryClient } from '@tanstack/react-query';
import { IconLocationBroken } from '@tabler/icons-react';
import { match } from 'ts-pattern';

import { Button, ButtonLink } from '~/domains/design-system/Button';

import StatusModalBody from './components/StatusModalBody';

export type LocationRestrictionVariant = 'contest' | 'predictionMarkets';

const permittedLocationsLink = (
  <ButtonLink mode="inlineLink" href="https://splashsports.com/where-can-you-play" target="_blank">
    {null}
  </ButtonLink>
);

const contactSupportLink = (
  <ButtonLink
    mode="inlineLink"
    href="mailto:support@splashsports.com?subject=Location Restriction"
    target="_blank"
  >
    {null}
  </ButtonLink>
);

function LocationRestrictionModal({
  isOpen,
  onClose,
  detectedLocation,
  desiredAction,
  queryKey,
  variant = 'contest',
  onRecheckLocation,
}: {
  isOpen: boolean;
  onClose: (isSubmit?: boolean) => void;
  detectedLocation: string;
  desiredAction?: string;
  queryKey: string[];
  variant?: LocationRestrictionVariant;
  onRecheckLocation?: () => Promise<boolean>;
}) {
  const { t } = useTranslation('contest');
  const [isRechecking, setIsRechecking] = useState(false);

  const isFetching = useIsFetching({ queryKey });
  const queryClient = useQueryClient();

  const handleRecheckLocationClick = async () => {
    if (!onRecheckLocation) {
      queryClient.invalidateQueries({ queryKey });
      return;
    }

    setIsRechecking(true);
    const isAllowed = await onRecheckLocation().finally(() => setIsRechecking(false));

    if (isAllowed) onClose();
  };

  const { text, text2 } = match(variant)
    .with('contest', () => ({
      text: (
        <Trans
          i18nKey="contest:common.userStatusModals.locationRestriction.text"
          values={{ detectedLocation, desiredAction }}
          components={{ link: permittedLocationsLink }}
        />
      ),
      text2: (
        <Trans
          i18nKey="contest:common.userStatusModals.locationRestriction.note"
          components={{ bold: <strong /> }}
        />
      ),
    }))
    .with('predictionMarkets', () => ({
      text: (
        <Trans
          i18nKey="contest:common.userStatusModals.locationRestriction.textPredictionMarkets"
          values={{ detectedLocation }}
          components={{ link: permittedLocationsLink }}
        />
      ),
      text2: (
        <Trans
          i18nKey="contest:common.userStatusModals.common.contactSupport"
          components={{ link: contactSupportLink }}
        />
      ),
    }))
    .exhaustive();

  return (
    <StatusModalBody
      isOpen={isOpen}
      onClose={onClose}
      icon={<IconLocationBroken size={40} stroke={1.5} />}
      title={t('common.userStatusModals.locationRestriction.title')}
      text={text}
      text2={text2}
      cta={
        <Button
          variant="secondary-fill"
          fullWidth
          onClick={handleRecheckLocationClick}
          loading={isRechecking || isFetching > 0}
        >
          {t('common.userStatusModals.common.recheckLocationCta')}
        </Button>
      }
    />
  );
}

export default LocationRestrictionModal;
